import { logError } from '@/composables';
import { showToast } from '@/composables/swal';
import { ApiResponse } from '@/models';
import Axios, { AxiosError, AxiosInstance } from 'axios';

const base_url = import.meta.env.VITE_APP_BASE_URL;

const http: AxiosInstance = Axios.create({
  baseURL: import.meta.env.VITE_APP_BASE_URL,
  headers: {
    'Content-type': 'application/json',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

const noTokenRequest: AxiosInstance = Axios.create({
  baseURL: base_url,
  headers: {
    'Content-type': 'application/json',
    'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

http.interceptors.request.use((request) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    request.headers.Authorization = `Bearer ${token}`;
  }
  return request;
});

/**
 * AB#21297 — a failed request does not always carry a response.
 *
 * A network drop, a timeout, a request the browser aborts on navigation, or a
 * search the app cancelled itself: in each of these `error.response` is
 * undefined. This interceptor used to read `error.response.status` regardless,
 * so every one of them threw a TypeError of its own — and that TypeError, not
 * the original failure, is what reached the caller's catch block and was shown
 * to the user as "Cannot read properties of undefined (reading 'status')".
 *
 * The 401 redirect is the only thing this interceptor is for. Everything else
 * is passed on unchanged, response or no response.
 */
http.interceptors.response.use(
  function (response) {
    return response;
  },
  function (error) {
    if (error?.response?.status === 401) {
      window.location.href = `${import.meta.env.VITE_VUE2_APP_URL}/login`;
    }
    return Promise.reject(error);
  }
);

/** What to tell a person when a request failed without the server answering. */
const NETWORK_MESSAGE = 'Could not reach the server. Check your connection and try again.';

const handleResponseException = (
  errorResponse: AxiosError<ApiResponse<null>> | TypeError
): void => {
  // A request the app cancelled on purpose — a superseded search, a page left
  // mid-load — is not an error anyone needs to hear about.
  if (Axios.isCancel(errorResponse)) {
    return;
  }

  let message: string | undefined = '';
  if ('isAxiosError' in errorResponse) {
    message = errorResponse.response?.data?.message;
    if (!message) {
      message = errorResponse.response?.statusText;
    }
    if (!message) {
      // No response at all: say what happened in words, not the transport
      // code, and never leave the toast blank.
      message = NETWORK_MESSAGE;
    }
    showToast(message, 'error');
    logError('Error (Axios): ', message);
  } else {
    message = errorResponse.message;
    showToast(message, 'error');
    logError(`Error (${errorResponse.name}): `, {
      name: errorResponse.name,
      message: errorResponse.message,
      stack: errorResponse.stack,
    });
  }
  logError('Error (Axios): ', message);
};

export { handleResponseException, http, noTokenRequest };
